import React, { useEffect, useState } from 'react';

/**
 * "There is more below" cue for a scrolling dialog (Paulo 2026-10-03: the
 * share dialog's Email / WhatsApp / X buttons sat below the fold, and people
 * might never find them).
 *
 * Render it as the LAST child inside the scroll container. While content
 * continues below the visible area, it sticks to the bottom edge with a soft
 * white fade and a round ↓ button; each tap scrolls down most of a page. It
 * disappears once the end is in view, and never shows when everything fits.
 * Pair it with the `scroll-visible` class on the container (index.css) so
 * the scrollbar itself stays visible on desktop. Convention:
 * docs/codebase/ui-conventions.md, "Dialogs".
 *
 * `bleed` = the container's horizontal padding, so the fade covers it.
 */
const ScrollMoreHint: React.FC<{ scrollRef: React.RefObject<HTMLElement | null>; bleed?: number }> = ({ scrollRef, bleed = 20 }) => {
  const [more, setMore] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = () => setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 12);
    update();
    el.addEventListener('scroll', update, { passive: true });
    // Size changes (window resize, a dialog opening) and content changes
    // (settings loaded, the email panel opened) both move the end.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    Array.from(el.children).forEach((child) => ro?.observe(child));
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
      mo.disconnect();
    };
  }, [scrollRef]);

  if (!more) return null;

  const scrollDown = () => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ top: Math.max(120, el.clientHeight * 0.8), behavior: 'smooth' });
  };

  return (
    // Height 0 + sticky: pinned to the bottom of the visible area without
    // taking space in the content.
    <div style={{ position: 'sticky', bottom: 0, height: 0, zIndex: 2 }}>
      <div
        aria-hidden
        style={{
          position: 'absolute', left: -bleed, right: -bleed, bottom: 0, height: 56,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0), #ffffff 85%)',
          pointerEvents: 'none',
        }}
      />
      <button
        type="button"
        onClick={scrollDown}
        aria-label="Show more"
        style={{
          position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)',
          width: 36, height: 36, padding: 0, borderRadius: '50%',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid rgba(20,40,30,0.12)', background: '#ffffff', color: '#157a55',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.18)', cursor: 'pointer',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

export default ScrollMoreHint;
